import React from "react";

import { cn } from "src/lib/utils";

/**
 * Lumonic branded loading spinner.
 *
 * Animation is defined via a one-time injected <style> block (or static CSS
 * in contexts that provide it, e.g. the Excel add-in's addin.css).
 *
 * - `contain: strict` on the SVG isolates it from surrounding layout
 * - `isolation: isolate` forces its own compositor layer
 * - opacity-only animation is always GPU-composited
 * - React.memo prevents re-renders from parent state churn
 */

/* ── one-time style injection ─────────────────────────── */

let stylesInjected = false;

function ensureStyles() {
  if (stylesInjected || document.querySelector(".lu-anim")) return;
  stylesInjected = true;

  const style = document.createElement("style");
  style.textContent = `
    @keyframes lu-pulse {
      0%        { opacity: 0.07; }
      18%, 38%  { opacity: 1; }
      62%, 100% { opacity: 0.07; }
    }
    .lu-anim {
      opacity: 0.07;
      will-change: opacity;
      animation: lu-pulse var(--lu-dur) ease infinite both;
    }
    .lu-spinner {
      contain: strict;
      isolation: isolate;
    }
  `;
  document.head.appendChild(style);
}

/* ── grid geometry ─────────────────────────────────────── */

const CELL = 26;
const GAP = 5;
const PAD = 4;
const STRIDE = CELL + GAP;
const VIEW = PAD * 2 + CELL * 3 + GAP * 2; // 96
const RX = 2;

const xy = (c: number, r: number) => ({
  x: PAD + c * STRIDE,
  y: PAD + r * STRIDE,
});

const CELLS = [
  { c: 1, r: 0, i: 0 },
  { c: 2, r: 0, i: 1 },
  { c: 1, r: 1, i: 2 },
  { c: 2, r: 1, i: 3 },
  { c: 2, r: 2, i: 4 },
];

const L_PATH = (() => {
  const x0 = PAD;
  const x1 = PAD + CELL;
  const x2 = PAD + CELL + GAP + CELL;
  const y0 = PAD;
  const y1 = PAD + 2 * STRIDE;
  const y2 = PAD + 3 * CELL + 2 * GAP;
  return `M${x0} ${y0} H${x1} V${y1} H${x2} V${y2} H${x0} Z`;
})();

const L_ORDER = 5;

const SIZE_PX: Record<string, number> = {
  small: 26,
  medium: 38,
  large: 54,
};

const DURATIONS: Record<string, number> = {
  fast: 1.4,
  normal: 2,
  slow: 2.8,
};

const DELAY_STEP = 0.04;
const DEFAULT_COLOR = "currentColor";

/* ── component ─────────────────────────────────────────── */

interface LoaderProps {
  /** Preset name or arbitrary pixel value. */
  size?: "small" | "medium" | "large" | number;
  label?: string | null;
  color?: string;
  speed?: "fast" | "normal" | "slow";
  className?: string;
}

const Loader = React.memo(({
  size = "medium",
  label = "Loading...",
  color = DEFAULT_COLOR,
  speed = "normal",
  className,
}: LoaderProps) => {
  ensureStyles();

  const px = typeof size === "number" ? size : SIZE_PX[size];
  const dur = DURATIONS[speed];
  const step = dur * DELAY_STEP;
  const durVal = `${dur}s`;
  // Negative delay syncs animation to wall-clock so sequential
  // Loader remounts continue seamlessly instead of restarting.
  const epoch = (performance.now() / 1000) % dur;

  return (
    <div className={cn("flex flex-col items-center justify-center w-full h-full gap-2.5", className)}>
      <svg
        className="lu-spinner"
        width={px}
        height={px}
        viewBox={`0 0 ${VIEW} ${VIEW}`}
        fill="none"
        role="status"
        aria-label="Loading"
      >
        {CELLS.map(({ c, r, i }) => {
          const { x, y } = xy(c, r);
          return (
            <rect
              key={`c${c}${r}`}
              className="lu-anim"
              x={x}
              y={y}
              width={CELL}
              height={CELL}
              rx={RX}
              fill={color}
              style={{ animationDelay: `${i * step - epoch}s`, "--lu-dur": durVal } as React.CSSProperties}
            />
          );
        })}

        <path
          className="lu-anim"
          d={L_PATH}
          fill={color}
          style={{ animationDelay: `${L_ORDER * step - epoch}s`, "--lu-dur": durVal } as React.CSSProperties}
        />
      </svg>

      {label && (
        <span className="text-[13px] font-medium opacity-50">
          {label}
        </span>
      )}
    </div>
  );
});

Loader.displayName = "Loader";

export default Loader;
